import { Adresse, Bedriftsdata, CreateOrderOfferRequest, CreateOrderOfferResponse, Kundetype, ProduktSpesifikasjon } from "codegen";
import { addDays, addMonths, format } from "date-fns";
import { nb } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";
import StyledText from "@ntemarked/mittne-components/dist/StyledText/StyledText";

import Warning from "components/molecules/Warning";
import LoadingAnimation from "components/organisms/LoadingAnimation";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import Form from "components/organisms/form/Form";
import SelectField from "components/organisms/form/SelectField";
import TextField from "components/organisms/form/TextField";
import ToggleField from "components/organisms/form/ToggleField";
import useProducts, { mapCustomerType } from "hooks/useProducts";
import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { newOfferString } from "pages/Strombestilling/orderoffers/OrderOffersPage";
import { emailUnsubscribedMessage } from "pages/Strombestilling/orderoffers/orderOfferUtils";
import AddressSearchTextField, { geoNorgeAddressToMaalerAdresse, validateFakturaAdresse } from "pages/Strombestilling/orders/components/AddressSearchTextField";
import CompanySearch from "pages/Strombestilling/orders/components/CompanySearch";
import MeterSearch, { MaalerMedValidering } from "pages/Strombestilling/orders/components/MeterSearch";
import PersonSearch from "pages/Strombestilling/orders/components/PersonSearch";
import { getEmailValidationError } from "utils/EmailValidator";
import { subWorkingDays } from "utils/NorwegianDateUtil";
import { capitalizeFirstLetterRestLower, validateFodselsnummer } from "utils/Utils";

export enum OrderOfferChannel {
	KS_CHAT = "WEB/kundesenterchat",
	KS_EPOST = "WEB/kundesenterepost",
	KS_TELEFON = "WEB/kundesentertelefon",
}

const sellableProducts = [
	"standard-spotpris-privat",
	"kundekontrakt-webspot-6mnd",
	"agrol-inn",
	"bli-spot",
	"standard-spotpris-bedrift",
	"agrol-spotpris-bedrift",
	"firma-spot",
];

const channelmap: Map<OrderOfferChannel, string> = new Map([
	[OrderOfferChannel.KS_CHAT, "Chat"],
	[OrderOfferChannel.KS_EPOST, "Epost"],
	[OrderOfferChannel.KS_TELEFON, "Telefon"],
]);

// z.coerce.date() tolker "yyyy-MM-dd" fra datofeltet som UTC-midnatt, så grensene må sammenlignes på samme måte
const toUtcMidnight = (date: Date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

const OrderOfferSchema = (minDate: Date, maxDate: Date, isPrivate: boolean, minDateValidationMessage: string) => {
	return z
		.object({
			startDate: z.coerce
				.date({
					message: "Oppstartsdato er påkrevd og må være et gyldig datoformat",
				})
				.min(toUtcMidnight(minDate), {
					message: minDateValidationMessage + ` Tidligste tilgjengelige dato er ${format(minDate, "dd.MM.yyyy", { locale: nb })}`,
				})
				.max(toUtcMidnight(maxDate), {
					message: `Oppstartsdato kan ikke være mer enn seks måneder frem i tid. Seneste tilgjengelige dato er ${format(maxDate, "dd.MM.yyyy", { locale: nb })}`,
				}),
			firstName: isPrivate ? z.string().min(1, { message: "Fornavn må være minst 1 tegn" }) : z.string().optional().or(z.literal("")),
			lastName: isPrivate ? z.string().min(1, { message: "Etternavn må være minst 1 tegn" }) : z.string().optional().or(z.literal("")),
			phone: z.string().regex(/^[\d\s+-]{8,12}$/, "Bruk telefonnummer-format som 12345678 eller +4712345678"),
			email: z
				.string()
				.optional()
				.or(z.literal(""))
				.refine((val) => val === "" || val === undefined || getEmailValidationError(val) === null, {
					message: "Vennligst oppgi en gyldig e-postadresse, eller la feltet stå tomt.",
				}),
			invoiceAddress: z.string().min(2, { message: "Fakturaadresse må være minst 2 tegn" }).optional().or(z.literal("")),
			channel: z.enum(Object.values(OrderOfferChannel) as [string, ...string[]], {
				message: "Vennligst velg salgskanal",
			}),
			sendSms: z.boolean(),
			sendEmail: z.boolean(),
			overrideCreditCheck: z.boolean(),
		})
		.refine((data) => data.sendSms || data.sendEmail, {
			message: "SMS eller e-post må velges",
			path: ["sendEmail"],
		})
		.refine((data) => !data.sendEmail || (data.email && data.email.trim().length > 0), {
			message: "E-postadresse er påkrevd når 'Send på e-post' er valgt",
			path: ["email"],
		});
};

export default function OfferNewPage() {
	const [company, setCompany] = useState<Bedriftsdata | undefined>(undefined);
	const { offerId } = useParams<{ offerId: string }>();
	const isNewOffer = useMemo(() => {
		return offerId === newOfferString;
	}, [offerId]);

	const today = useMemo(() => new Date(), []);
	/*
		Støtte for redigering av eksisterende tilbud:
		- Hent tilbudsdata her ved bruk av offerId
		- Fyll inn skjemaet med hentede data
		- Ved innsending av tilbud, naviger til denne sida med den nye offerId'en
		-- okTime må justeres til å hente fra eksisterende tilbud
	*/

	// Målersøk
	const [selectedProduct, setSelectedProduct] = useState<ProduktSpesifikasjon | undefined>(undefined);
	const [selectedMeter, setSelectedMeter] = useState<MaalerMedValidering | null>(null);

	// Kundeinfo
	const [fakturaAdresse, setFakturaAdresse] = useState<Adresse | null>(null);
	const [fodselsnummer, setFodselsnummer] = useState<string | undefined>(undefined);
	// Navnefeltene er kontrollerte, så personsøket kan fylle dem ut
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");

	// Form state
	const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
	const [okTime, setOkTime] = useState<Date | null>(null);
	const [formError, setFormError] = useState<string | null>(null);
	const [startDate, setStartDate] = useState<string>("");
	// Satt når kunden har reservert seg mot e-post. sentBySms sier om tilbudet likevel ble sendt på SMS.
	const [emailUnsubscribed, setEmailUnsubscribed] = useState<{ sentBySms: boolean } | null>(null);

	const isCompany = useMemo(() => selectedProduct?.kundetype === Kundetype.Bedrift, [selectedProduct]);

	// Advarsel ved navigering bort fra siden med endringer i skjemaet. NB: Funker ikke ved tilbakenavigering
	useEffect(() => {
		function handlePageUnload(e: BeforeUnloadEvent) {
			e.preventDefault();
		}
		if (isFormChanged) {
			globalThis.addEventListener("beforeunload", handlePageUnload);
		}
		return () => {
			globalThis.removeEventListener("beforeunload", handlePageUnload);
		};
	}, [isFormChanged]);

	// Hent selgbare produkter
	const {
		productSelectOptions: products,
		isLoadingProducts: loadingProducts,
		productsError: productError,
	} = useProducts({ productOptionsTariffIds: new Set(sellableProducts) });

	// Innsending av strømtilbud
	const { mutateAsync: submitOrderOffer, isPending: isSubmittingOrder } = useStrombestillingApiMutation("/orderoffer", ["orderoffers"], "POST", true);

	const canBeStartedBackInTime = Boolean(selectedMeter) && !selectedMeter?.erEier && (validateFodselsnummer(fodselsnummer) || Boolean(company?.orgnr));
	const minDate = canBeStartedBackInTime ? subWorkingDays(today, 30) : addDays(today, 3);
	const maxDate = addMonths(today, 6);
	const minDateValidationMessage = canBeStartedBackInTime
		? "Startdato må være maks 30 virkedager tilbake i tid."
		: "Startdato må være minst tre dager frem i tid.";

	if (productError) {
		return <p>Feil ved henting av produkter: {productError?.message}</p>;
	}
	if (loadingProducts) {
		return <LoadingAnimation />;
	}

	return (
		<PageWrapper direction={Direction.column} gap="1rem">
			<MainLayoutPageHeader title={`${isNewOffer ? "Opprett" : "Rediger"} tilbud om strømavtale`} />
			<Form
				schema={OrderOfferSchema(minDate, maxDate, !isCompany, minDateValidationMessage)}
				loading={loadingProducts}
				onError={() => setFormError("Det er feil i skjemaet, og tilbudet ble ikke sendt inn. Vennligst se over.")}
				onChange={() => setIsFormChanged(true)}
				onSubmit={async (data) => {
					setFormError(null);
					setEmailUnsubscribed(null);
					if (isSubmittingOrder) {
						return;
					}
					if (!selectedProduct) {
						setFormError("Vennligst velg et produkt");
						return;
					}
					if (!fodselsnummer) {
						setFormError("Finn kunden: skriv inn fødselsnummer, eller søk etter personen med navn og fødselsdato.");
						return;
					}
					if (!validateFodselsnummer(fodselsnummer)) {
						setFormError("Fødselsnummer er ikke gyldig");
						return;
					}
					if (isCompany && !company) {
						setFormError("Vennligst finn en bedrift.");
						return;
					}
					if (!selectedMeter) {
						setFormError("Vennligst velg en måler ved å søke etter målernummer, målepunkt-ID eller adresse");
						return;
					}
					if (selectedMeter.validation && !selectedMeter.validation.isValid) {
						setFormError(`Den valgte måleren er ikke gyldig: ${selectedMeter.validation.message}`);
						return;
					}
					if (data.invoiceAddress && !fakturaAdresse) {
						setFormError("Vennligst velg en fakturaadresse fra nedtrekksmenyen");
						return;
					}
					const fakturaValidation = validateFakturaAdresse(fakturaAdresse);
					if (fakturaValidation !== null) {
						setFormError(fakturaValidation);
						return;
					}

					const request: CreateOrderOfferRequest = {
						kilde: data.channel as OrderOfferChannel,
						tariffId: selectedProduct?.tariffId ?? "",
						meteringPointId: selectedMeter.maalepunktId!,
						meterNumber: selectedMeter.maalernummer!,
						levering: {
							adresse: {
								gate: selectedMeter.adresse?.gate!,
								husnummer: selectedMeter.adresse?.husnummer || undefined,
								postnummer: selectedMeter.adresse?.postnummer!,
								poststed: selectedMeter.adresse?.poststed!,
							},
						},
						oppstartsdato: format(data.startDate, "yyyy-MM-dd"),
						kunde: {
							fornavn: data?.firstName?.trim(),
							etternavn: data?.lastName?.trim(),
							mobil: data.phone.trim(),
							epost: data.email ? data.email.trim() : undefined,
							adresse: fakturaAdresse
								? {
										gate: fakturaAdresse.gate,
										husnummer: fakturaAdresse.husnummer || undefined,
										postnummer: fakturaAdresse.postnummer,
										poststed: fakturaAdresse.poststed,
									}
								: undefined,
							organisasjonsnummer: company?.orgnr?.trim(),
							fodselsnummer,
						},
						sendSMS: data.sendSms,
						sendEmail: data.sendEmail,
						overrideCreditCheck: data.overrideCreditCheck,
					};
					submitOrderOffer(request)
						.then(async (response) => {
							if (response.ok) {
								const body: CreateOrderOfferResponse = await response.json();
								setEmailUnsubscribed(body.epostReservert ? { sentBySms: data.sendSms } : null);
								setOkTime(new Date());
							} else {
								setFormError("Noe gikk galt ved sending av tilbudet");
							}
						})
						.catch((error) => {
							setFormError(`Noe gikk galt ved sending av tilbudet: ${error.message}`);
						})
						.finally(() => {
							setIsFormChanged(false); // Reset form changed state after submission
						});
				}}
			>
				<Flex direction={Direction.column} gap="1rem">
					<SelectField
						name="product"
						label="Produkt"
						options={[
							{ label: "Velg produkt", value: "" },
							...products.map((product) => {
								return {
									value: product.tariffId ?? "",
									label: `${product.navn} (${capitalizeFirstLetterRestLower(mapCustomerType(product.kundetype))})`,
								};
							}),
						]}
						value={selectedProduct?.tariffId ?? ""}
						onChange={(product) => {
							setSelectedMeter(null);

							const newSelectedProduct = products.find((p) => p.tariffId === product);
							if (newSelectedProduct?.kundetype !== selectedProduct?.kundetype) {
								setCompany(undefined);
							}
							setSelectedProduct(newSelectedProduct);
						}}
						required
					/>

					<PersonSearch
						fodselsnummer={fodselsnummer}
						setFodselsnummer={(fodselsnummer) => {
							setFodselsnummer(fodselsnummer);
							setStartDate("");
						}}
						onPersonFound={(person) => {
							// For privat er personen kunden, så navnet fylles ut i kontaktinformasjonen. For bedrift er det bare signataren
							if (isCompany) return;
							setFirstName(person.fornavn ?? "");
							setLastName(person.etternavn ?? "");
						}}
					/>
					{isCompany && (
						<CompanySearch
							company={company}
							setCompany={(company) => {
								setCompany(company);
								setStartDate("");
								if (!fakturaAdresse && company) {
									const companyValidationError = validateFakturaAdresse(company?.adresse ?? null);
									setFormError(companyValidationError);
									setFakturaAdresse(company?.adresse ?? null);
								}
							}}
						/>
					)}
					<MeterSearch
						product={selectedProduct}
						selectedMeter={selectedMeter}
						fodselsnummer={fodselsnummer}
						orgnummer={company?.orgnr}
						setSelectedMeter={(meter) => {
							setIsFormChanged(true);
							setSelectedMeter(meter);
							setStartDate("");
							if (!fakturaAdresse && !isCompany && meter) {
								setFakturaAdresse(meter.adresse!);
							}
						}}
					/>
					{canBeStartedBackInTime && <StyledText>Kundebytte: Oppstart tilbake i tid støttes for denne måleren.</StyledText>}
					<TextField
						name="startDate"
						label="Oppstartsdato"
						type="date"
						disabled={!selectedMeter}
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						min={format(minDate, "yyyy-MM-dd")}
						max={format(maxDate, "yyyy-MM-dd")}
						required
						autoComplete="off"
					/>

					<h2 style={{ marginTop: "1rem" }}>Kontaktinformasjon</h2>
					{isCompany ? (
						<TextField name="companyName" label="Firmanavn" value={company?.navn ?? ""} readOnly disabled required />
					) : (
						<Flex gap="0.5rem">
							<TextField
								name="firstName"
								label="Fornavn"
								description="Fullt navn blir satt av BankID i den fullførte ordren"
								autoComplete="off"
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
								required
							/>
							<TextField
								name="lastName"
								label="Etternavn"
								autoComplete="off"
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
								required
							/>
						</Flex>
					)}
					<TextField
						name="phone"
						label="Telefonnummer"
						description="Helst les telefonnummeret tilbake til kunden så vi er sikre på at vi sender SMS til rett person"
						type="tel"
						autoComplete="off"
						required
					/>
					<TextField
						name="email"
						label="E-postadresse"
						description="E-post kan også fylles ut eller endres i kundens del av løsningen"
						type="email"
						autoComplete="off"
					/>
					<AddressSearchTextField
						name="invoiceAddress"
						label="Fakturaadresse"
						description={
							isCompany
								? "Blir automatisk bedriftsadressen. Kan endres både her og i kundens del av løsningen"
								: "Blir automatisk leveringsadresse (målerens adresse) når måler velges. Kan endres både her og i kundens del av løsningen"
						}
						// Heads up; om fakturaadresse blir satt via målervalg, og slettet, og måler blir valgt igjen, får denne en "usynlig" state som er måleradressen fra andre valget.
						// Såpass usannsynlig at det skjer at jeg lar det være for nå.
						defaultValue={fakturaAdresse?.asString}
						setAddress={(nyAdresse) => {
							setIsFormChanged(true);
							if (nyAdresse) {
								const adresse = geoNorgeAddressToMaalerAdresse(nyAdresse);
								setFakturaAdresse(adresse);
								const companyValidationError = validateFakturaAdresse(adresse);
								setFormError(companyValidationError);
							} else {
								setFakturaAdresse(null);
							}
						}}
					/>
					<Flex direction={Direction.column} gap="1rem">
						<h2 style={{ marginTop: "1rem" }}>Innstillinger</h2>
						<SelectField
							name="channel"
							label="Salgskanal"
							options={[
								{ label: "Velg salgskanal", value: "" },
								...Object.values(OrderOfferChannel).map((p) => ({
									value: p.toString(),
									label: channelmap.get(p) ?? "",
								})),
							]}
							required
						/>
						<ToggleField name="sendSms" label={"Send på SMS"} defaultChecked={true} />
						<ToggleField name="sendEmail" label={"Send på e-post"} />
						<ToggleField name="overrideCreditCheck" label={"Overstyr kredittsjekk"} />
					</Flex>
					{formError && <ErrorMessage>{formError}</ErrorMessage>}
					{okTime && !formError && (
						<StyledText size="xlarge" style={{ marginTop: "1rem" }}>
							Tilbud {emailUnsubscribed && !emailUnsubscribed.sentBySms ? "opprettet" : "sendt"}{" "}
							{format(okTime, "d. MMMM yyyy, HH:mm", { locale: nb })}
						</StyledText>
					)}
					{okTime && !formError && emailUnsubscribed && <Warning warningMessage={emailUnsubscribedMessage(emailUnsubscribed.sentBySms)} />}
					<Button
						submit
						loading={isSubmittingOrder}
						label={isSubmittingOrder ? "Sender tilbud..." : "Send tilbud"}
						style={{ marginTop: "1rem" }}
						disabled={!isFormChanged}
					/>
				</Flex>
			</Form>
		</PageWrapper>
	);
}

const PageWrapper = styled(Flex)`
	max-width: 800px;
`;

const ErrorMessage = styled.p`
	color: var(--varsel-roed);
`;
